import React from "react";

const fmtDate = (d) => {
  if (!d) return new Date().toLocaleDateString("rw-RW", { year: "numeric", month: "long", day: "numeric" });
  return new Date(d).toLocaleDateString("rw-RW", { year: "numeric", month: "long", day: "numeric" });
};

const fmtShortDate = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()}`;
};

// Renders a filled value underlined (as if written on the printed blank), or
// a literal dotted blank when the value hasn't been provided yet.
const Blank = ({ value, min = 18 }) => {
  const has = value !== undefined && value !== null && String(value).trim() !== "";
  if (has) return <span className="imico-blank-filled">{String(value)}</span>;
  return <span className="imico-blank-empty">{"…".repeat(min)}</span>;
};

// Title of the certifying official, matched to their actual role.
const signerTitle = (role, akagariName) => {
  const r = (role || "").toLowerCase();
  if (r === "umunyamabanga") return <>Umunyamabanga Nshingwabikorwa w'Akagari ka <Blank value={akagariName} min={12} /></>;
  if (r === "isibo") return <>Umukuru w'Isibo, mu Kagari ka <Blank value={akagariName} min={12} /></>;
  return <>Umunyamabanga Nshingwabikorwa w'Akagari ka <Blank value={akagariName} min={12} /></>;
};

/**
 * Pure, logo-free, text-only certificate layout rendering the official
 * "Icyemezo cy'Imico n'Imyifatire" narrative text with real data filling the
 * blanks. Used both for the live on-screen preview and for print
 * (@media print CSS lives in ImicoImyifatire.jsx). Never renders an <img>
 * for anything other than a captured signature.
 */
export default function ImicoCertificatePreview({ cert, signatures = [] }) {
  const firstSig = signatures.find((s) => s.level === "first");
  const villageSig = signatures.find((s) => s.level === "village");
  const cellSig = signatures.find((s) => s.level === "cell");
  const signerName = firstSig?.signer_name || cert.created_by_name;
  const signerRole = firstSig?.signer_role || cert.created_by_role;

  return (
    <div className="imico-paper" id="imico-print-area">
      <div className="imico-doc-header">
        <div className="imico-doc-republic">Repubulika y'u Rwanda</div>
        <div className="imico-doc-title">Icyemezo Gitangwa n'Ubuyobozi bw'Akagari</div>
        {cert.doc_number && <div className="imico-doc-number">{cert.doc_number}</div>}
      </div>

      <div className="imico-doc-narrative">
        <p>
          Njyewe <Blank value={signerName} />&nbsp;
          {signerTitle(signerRole, cert.akagari_name)}, nshingiye ku makuru yatanzwe
          n'ubuyobozi bw'umudugudu wa <Blank value={cert.umudugudu_name} min={14} /> ndemeza
          ko uyu <Blank value={cert.applicant_name} min={22} /> mwene{" "}
          <Blank value={cert.father_name} min={16} /> Na <Blank value={cert.mother_name} min={16} /> wavukiye
          mu Karere ka <Blank value={cert.birth_akarere} min={12} /> Umurenge wa{" "}
          <Blank value={cert.birth_umurenge} min={12} /> /Akagari ka <Blank value={cert.birth_akagari} min={12} /> wavutse
          kuwa <Blank value={fmtShortDate(cert.applicant_dob)} min={10} /> Ufite Indangamuntu n°{" "}
          <Blank value={cert.applicant_national_id} min={16} /> yatangiwe mu Karere ka{" "}
          <Blank value={cert.id_akarere} min={12} /> Umurenge wa <Blank value={cert.id_umurenge} min={12} />. Ni
          umuturage utuye/ukodesha mu Kagari ka <Blank value={cert.akagari_name} min={12} /> umudugudu
          wa <Blank value={cert.umudugudu_name} min={12} /> akaba ari indakemwa mu mico n'imyifatire.
        </p>
      </div>

      <div className="imico-doc-footer-meta">
        <span>Bikorewe i {cert.akagari_name || "—"}, kuwa {fmtDate(cert.created_at)}</span>
      </div>

      <div className="imico-doc-signatures">
        <div className="imico-doc-sig-block">
          {firstSig?.signature_image
            ? <img src={firstSig.signature_image} alt="Umukono" className="imico-doc-sig-img" />
            : <div className="imico-doc-sig-line" />}
          <div className="imico-doc-sig-name">{signerName || "—"}</div>
          <div className="imico-doc-sig-role">{signerTitle(signerRole, cert.akagari_name)}</div>
          {firstSig?.signed_at && <div className="imico-doc-sig-date">{fmtDate(firstSig.signed_at)}</div>}
        </div>
        <div className="imico-doc-sig-block">
          {villageSig?.signature_image
            ? <img src={villageSig.signature_image} alt="Umukono" className="imico-doc-sig-img" />
            : <div className="imico-doc-sig-line" />}
          <div className="imico-doc-sig-name">{villageSig?.signer_name || "—"}</div>
          <div className="imico-doc-sig-role">Umukuru w'Umudugudu wa {cert.umudugudu_name || "—"}</div>
          {villageSig?.signed_at && <div className="imico-doc-sig-date">{fmtDate(villageSig.signed_at)}</div>}
        </div>
        <div className="imico-doc-sig-block">
          {cellSig?.signature_image
            ? <img src={cellSig.signature_image} alt="Umukono" className="imico-doc-sig-img" />
            : <div className="imico-doc-sig-line" />}
          <div className="imico-doc-sig-name">{cellSig?.signer_name || "—"}</div>
          <div className="imico-doc-sig-role">Umunyamabanga Nshingwabikorwa w'Akagari ka {cert.akagari_name || "—"}</div>
          {cellSig?.signed_at && <div className="imico-doc-sig-date">{fmtDate(cellSig.signed_at)}</div>}
        </div>
      </div>
    </div>
  );
}
